import { i18n, TFunction } from 'i18next'
import React from 'react'
import { Translateable, TranslateableObject } from 'utils/translations'

type AnyObject = Record<never, unknown>

type EditableFields<T, K = T extends TranslateableObject<infer S> ? S : AnyObject> = Omit<
  T & K,
  keyof Translateable | 'id' | 'createdAt' | 'updatedAt'
>

type KeysOfType<T, K> = { [L in keyof T]: L extends K ? L : never }[keyof T]
type FieldsOfType<T, K, E = never> = {
  [L in KeysOfType<T, string>]: T[L] extends K ? (T[L] extends E ? never : L) : never
}[KeysOfType<T, string>]

type EditableFieldsOfType<T, K, E = never> = FieldsOfType<EditableFields<T>, K, E>

type ErrorFields<T> = {
  [L in keyof T]?: string
}
type TouchedFields<T> = {
  [L in keyof T]?: boolean
}
type DisabledFields<T> = {
  [L in keyof T]?: boolean
}

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>
export type NewOrExisting<T> = Optional<T, keyof T & 'id'>

export interface ContextValue<T extends AnyObject> {
  data: T
  valid: boolean
  errors: ErrorFields<EditableFields<T>>
  combinedErrors: string[]
  errFields: string[]
  touched: TouchedFields<EditableFields<T>>
  disabled: DisabledFields<EditableFields<T>>
  allTouched: boolean
  hasTranslation: boolean
  flatData: EditableFields<T>
  setFields: (change: { [key: string]: unknown }) => unknown
  onChange: (data: T) => unknown
  reloadTranslation: () => void
  handleSubmit: (e: React.KeyboardEvent) => void
  localeBase: string
  t: TFunction
  i18n: i18n
}

export type { AnyObject, EditableFields, EditableFieldsOfType, ErrorFields, TouchedFields, DisabledFields }

export const getFieldControlId = (field: string, id?: string, testId?: string) => id ?? testId ?? field
